import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar, Users, MapPin, Clock, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SEO } from '@/components/SEO';

const ShinrinYoku = () => {
  const navigate = useNavigate();

  const handleBooking = () => {
    navigate('/');
    setTimeout(() => window.scrollTo(0, 0), 100);
  };

  const handleGoBack = () => {
    navigate('/#upcoming-experiences');
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": "Shinrin Yoku - Forest Bathing for Beginners",
    "description": "Ancient Japanese healing practice of forest bathing. 3-hour mindful immersion in nature every Sunday. All levels welcome.",
    "startDate": "2024-01-01T10:00",
    "eventSchedule": {
      "@type": "Schedule",
      "repeatFrequency": "P1W",
      "byDay": "Sunday"
    },
    "location": {
      "@type": "Place",
      "name": "Ancient Oak Grove",
      "address": "Beskid Wyspowy, Poland"
    },
    "organizer": {
      "@type": "Organization",
      "name": "Mystic Forest",
      "url": "https://mysticforest.club"
    },
    "offers": {
      "@type": "Offer",
      "price": "45",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock"
    }
  };

  return (
    <>
      <SEO
        title="Shinrin Yoku - Japanese Forest Bathing Experience"
        description="Weekly 3-hour forest bathing sessions for beginners. Reduce stress, boost immunity, and enhance well-being through this ancient Japanese practice."
        canonicalUrl="https://mysticforest.club/experiences/shinrin-yoku"
        jsonLd={jsonLd}
      />
      
      <div className="min-h-screen bg-background">
        <Navigation />
      
      {/* Go Back Button */}
      <div className="pt-20 pb-4">
        <div className="max-w-6xl mx-auto px-6">
          <Button 
            variant="ghost" 
            onClick={handleGoBack}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Experiences
          </Button>
        </div>
      </div>
      
      {/* Hero Section */}
      <section className="relative py-32">
        <div className="absolute inset-0">
          <img 
            src="/lovable-uploads/9094ce1e-40b4-4f8a-aba4-850985c0efa9.png" 
            alt="Person practicing shinrin yoku forest bathing meditation among trees"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        
        <div className="relative max-w-4xl mx-auto px-6 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-serif font-light mb-6">
            Shinrin Yoku
          </h1>
          <p className="text-xl md:text-2xl mb-8 opacity-90">
            Forest Bathing for Beginners - Ancient Japanese Healing Practice
          </p>
          <Button size="lg" className="px-8 py-4 text-lg" onClick={handleBooking}>
            Enter the Forest
          </Button>
        </div>
      </section>

      {/* Quick Info */}
      <section className="py-16 bg-card">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div className="flex flex-col items-center">
              <Calendar className="w-8 h-8 mb-4 text-accent" />
              <h3 className="font-semibold mb-2">When</h3>
              <p className="text-muted-foreground">Every Sunday</p>
              <p className="text-sm text-muted-foreground">10 AM - 1 PM</p>
            </div>
            <div className="flex flex-col items-center">
              <MapPin className="w-8 h-8 mb-4 text-accent" />
              <h3 className="font-semibold mb-2">Where</h3>
              <p className="text-muted-foreground">Ancient Oak Grove</p>
            </div>
            <div className="flex flex-col items-center">
              <Users className="w-8 h-8 mb-4 text-accent" />
              <h3 className="font-semibold mb-2">For Whom</h3>
              <p className="text-muted-foreground">Complete beginners</p>
            </div>
            <div className="flex flex-col items-center">
              <Clock className="w-8 h-8 mb-4 text-accent" />
              <h3 className="font-semibold mb-2">Duration</h3>
              <p className="text-muted-foreground">3 Hours</p>
            </div>
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-serif font-light mb-8 text-center">
            The Medicine of Simply Being Among Trees
          </h2>
          <div className="prose prose-lg max-w-none text-muted-foreground">
            <p className="mb-6">
              Shinrin-yoku, literally "forest bathing," is a Japanese practice that involves mindfully immersing yourself in the forest atmosphere. This isn't hiking or exercise – it's a meditative practice of opening your senses to the forest's healing presence.
            </p>
            <p className="mb-6">
              Scientific research shows that spending mindful time among trees reduces stress hormones, boosts immune function, lowers blood pressure, and enhances overall well-being. The forest offers what our modern lives often lack: deep rest and natural connection.
            </p>
            <p className="mb-6">
              No experience necessary. No special equipment required. Just bring your curiosity and willingness to slow down to the speed of trees.
            </p>
          </div>
        </div>
      </section>

      {/* The Practice */}
      <section className="py-16 bg-card">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-serif font-light mb-12 text-center">
            What We'll Experience Together
          </h2>
          <div className="space-y-8">
            <Card>
              <CardHeader>
                <CardTitle>Opening Ceremony</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Begin with intention setting and gentle breathing to transition from "doing" mode to "being" mode.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Sensory Awakening</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Guided exercises to awaken your senses – touching bark, listening to leaves, breathing forest air mindfully.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Sit Spot Practice</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Find your special place in the forest and simply be present. Watch the forest watch you back.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Forest Tea Ceremony</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Close with mindful tea drinking among the trees, sharing insights and setting intentions for integration.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-serif font-light mb-12 text-center">
            Scientifically Proven Benefits
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <Card>
              <CardHeader>
                <CardTitle>Stress Reduction</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  Lowers cortisol levels and activates the parasympathetic nervous system for deep relaxation.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Immune Boost</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  Increases natural killer cell activity and anti-cancer proteins through exposure to phytoncides.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Mental Clarity</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  Improves focus, creativity, and cognitive function while reducing mental fatigue.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Heart Health</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  Reduces blood pressure and heart rate, promoting cardiovascular wellness.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Better Sleep</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  Regulates circadian rhythms and promotes deeper, more restorative sleep patterns.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Mood Enhancement</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  Reduces anxiety and depression while increasing feelings of vitality and well-being.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Investment */}
      <section className="py-16 bg-card">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-serif font-light mb-8">Investment</h2>
          <Card className="p-8">
            <div className="text-4xl font-semibold mb-4">$45</div>
            <p className="text-muted-foreground mb-6">Per session</p>
            <div className="text-sm text-muted-foreground mb-8">
              <p>Includes guided practice and forest tea</p>
              <p>All levels welcome - no experience needed</p>
              <p>Drop-in friendly or purchase a monthly pass</p>
            </div>
            <div className="space-y-4">
              <Button size="lg" className="w-full" onClick={handleBooking}>
                Join Next Session
              </Button>
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-2">Monthly Pass (4 sessions)</p>
                <Button variant="outline" size="lg" className="w-full" onClick={handleBooking}>
                  $150 - Save $30
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </section>

        <Footer />
      </div>
    </>
  );
};

export default ShinrinYoku;